import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { LMS_LESSON_TYPES, LmsLesson, LmsLessonContent, LmsLessonType } from '@/services/lms.api';
import { Modal } from '@/components/ui/Modal';
import { Field, Input, Select, Textarea, Checkbox } from '@/components/ui/Input';

const URL_TYPES: LmsLessonType[] = ['video', 'document', 'link', 'live'];

const TYPE_LABEL: Record<LmsLessonType, string> = {
  video: 'Video',
  document: 'Document',
  text: 'Text',
  link: 'Link',
  live: 'Live session',
};

const schema = z
  .object({
    type: z.enum(LMS_LESSON_TYPES),
    title: z.string().min(1, 'Required').max(200),
    isMandatory: z.boolean(),
    url: z.string().optional().or(z.literal('')),
    body: z.string().optional().or(z.literal('')),
    scheduledAt: z.string().optional().or(z.literal('')),
  })
  .superRefine((v, ctx) => {
    if (URL_TYPES.includes(v.type)) {
      if (!v.url?.trim()) {
        ctx.addIssue({ path: ['url'], code: z.ZodIssueCode.custom, message: 'URL is required' });
      } else {
        try {
          new URL(v.url);
        } catch {
          ctx.addIssue({ path: ['url'], code: z.ZodIssueCode.custom, message: 'Must be a valid URL' });
        }
      }
    }
    if (v.type === 'text' && !v.body?.trim()) {
      ctx.addIssue({ path: ['body'], code: z.ZodIssueCode.custom, message: 'Content is required' });
    }
    if (v.type === 'live' && !v.scheduledAt?.trim()) {
      ctx.addIssue({ path: ['scheduledAt'], code: z.ZodIssueCode.custom, message: 'Date & time is required' });
    }
  });
type FormValues = z.infer<typeof schema>;

export interface LessonSubmitValues {
  type: LmsLessonType;
  title: string;
  isMandatory: boolean;
  content: LmsLessonContent;
}

/** datetime-local's value has no timezone — normalize to a real ISO string
 * (interpreted as local time) before it reaches the backend's Date.parse check. */
function toIso(datetimeLocalValue: string): string {
  return new Date(datetimeLocalValue).toISOString();
}

function toDatetimeLocal(iso: string | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function contentFromLesson(lesson?: LmsLesson): Partial<FormValues> {
  if (!lesson) return {};
  const c = lesson.content as unknown as Record<string, unknown>;
  return {
    url: typeof c.url === 'string' ? c.url : '',
    body: typeof c.body === 'string' ? c.body : '',
    scheduledAt: typeof c.scheduledAt === 'string' ? toDatetimeLocal(c.scheduledAt) : '',
  };
}

function buildContent(v: FormValues): LmsLessonContent {
  switch (v.type) {
    case 'text':
      return { body: v.body ?? '' };
    case 'live':
      return { url: v.url ?? '', scheduledAt: toIso(v.scheduledAt ?? '') };
    default:
      return { url: v.url ?? '' };
  }
}

export function LmsLessonFormModal({
  open,
  lesson,
  onClose,
  onSubmit,
  saving,
}: {
  open: boolean;
  lesson?: LmsLesson;
  onClose: () => void;
  onSubmit: (v: LessonSubmitValues) => void;
  saving: boolean;
}) {
  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    values: {
      type: lesson?.type ?? 'text',
      title: lesson?.title ?? '',
      isMandatory: lesson?.isMandatory ?? true,
      url: '',
      body: '',
      scheduledAt: '',
      ...contentFromLesson(lesson),
    },
  });
  const type = watch('type');

  const submit = (v: FormValues) =>
    onSubmit({ type: v.type, title: v.title, isMandatory: v.isMandatory, content: buildContent(v) });

  return (
    <Modal
      open={open}
      onClose={() => {
        reset();
        onClose();
      }}
      title={lesson ? `Edit ${lesson.title}` : 'New lesson'}
      size="md"
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn-primary" onClick={handleSubmit(submit)} disabled={saving}>
            {saving ? 'Saving…' : lesson ? 'Save changes' : 'Add lesson'}
          </button>
        </>
      }
    >
      <form className="space-y-4">
        <Field label="Type" required hint={lesson ? "Type can't be changed after creation" : undefined}>
          {lesson ? (
            <Input value={TYPE_LABEL[lesson.type]} disabled />
          ) : (
            <Select {...register('type')}>
              {LMS_LESSON_TYPES.map((t) => (
                <option key={t} value={t}>
                  {TYPE_LABEL[t]}
                </option>
              ))}
            </Select>
          )}
        </Field>

        <Field label="Title" required error={errors.title?.message}>
          <Input {...register('title')} placeholder="Lesson title" />
        </Field>

        {URL_TYPES.includes(type) && (
          <Field label="URL" required error={errors.url?.message}>
            <Input {...register('url')} placeholder="https://…" />
          </Field>
        )}

        {type === 'text' && (
          <Field label="Content" required error={errors.body?.message}>
            <Textarea rows={6} {...register('body')} placeholder="Lesson text…" />
          </Field>
        )}

        {type === 'live' && (
          <Field label="Scheduled at" required error={errors.scheduledAt?.message}>
            <Input type="datetime-local" {...register('scheduledAt')} />
          </Field>
        )}

        <Checkbox label="Mandatory lesson" {...register('isMandatory')} />
      </form>
    </Modal>
  );
}
